import { DatePipe, NgFor, NgIf } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { PropertyGroupDescription } from '@grapecity/wijmo'
import { PopupPosition } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRange, CellRangeEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { Popup, PopupTrigger } from '@grapecity/wijmo.input'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    EquipmentBreakdown,
    EquipmentRequest,
    EquipmentTransfer,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BreakdownComponent } from './breakdown.component'
import { RequestComponent } from './request.component'
import { TransferComponent } from './transfer.component'

@Component({
    templateUrl: './availability.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridColumnGroupComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarButtonsComponent,
        NgFor,
        NgIf,
        NgbNavOutlet,
        DatePipe,
    ],
})
export class AvailabilityComponent extends BaseComponent implements OnInit, OnDestroy {
    private _activatedRoute = inject(ActivatedRoute)

    // UI and navigation
    faSearch = faSearch
    active = 1

    // Grid instances
    grid: Grid<any>
    requestGrid: Grid<EquipmentRequest>
    transferGrid: Grid<any>
    breakdownGrid: Grid<any>

    // Data collections
    allEquipment: any[] = []
    allJobs: any[] = []
    itemIds: any[] = []
    categories: any[] = []

    // Data maps for dropdowns
    jobMap!: DataMap
    allUserMap!: DataMap
    activeUserMap!: DataMap
    equipmentMap!: DataMap

    // Request/transfer state
    isAddingRequestFromEquipment = false
    linkedRequestId = ''
    onLoadMarkTransferComplete = false

    // Popup and UI interactions
    cellPopup!: Popup
    popupOrders: any[] = []
    currentCell!: any

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.EquipmentAvailabilityEdit])
    }

    constructor() {
        super()

        const tab = this.services.angular.getQueryParam('t', this._activatedRoute)
        if (tab) {
            this.active = +tab
            this.linkedRequestId = this.services.angular.getQueryParam('id', this._activatedRoute)
            this.onLoadMarkTransferComplete = this.services.angular.getQueryParam('complete', this._activatedRoute) === 'true'
        }

        this.grid = new Grid<any>(this.services, 'equipment', this)
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [item.equipment])
        this.requestGrid = new Grid<EquipmentRequest>(this.services)
            .setupModalEditing(
                this.services.data.equipmentRequests,
                'Equipment Request',
                RequestComponent,
                () => new EquipmentRequest(),
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
            .filterBy(item => [item.equipment])
        this.transferGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.equipmentTranfers,
                'Equipment Transfer',
                TransferComponent,
                () => new EquipmentTransfer(),
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders(80)
            .filterBy(item => [item.equipment])
        this.breakdownGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.equipmentBreakdowns,
                'Equipment Breakdown',
                BreakdownComponent,
                () => new EquipmentBreakdown(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.equipment])
            .wrapColumnHeaders()

        this.requestGrid.modified.on(this.refresh.bind(this))
        this.breakdownGrid.modified.on(this.refresh.bind(this))

        this.refresh()
    }

    ngOnInit() {
        this.cellPopup = new Popup('#popup', {
            position: PopupPosition.Left,
            removeOnHide: false,
            fadeIn: false,
            fadeOut: false,
            isDraggable: false,
            hideTrigger: PopupTrigger.LeaveOwner | PopupTrigger.LeavePopup,
            showTrigger: PopupTrigger.None,
        })
    }

    ngOnDestroy() {
        this.cellPopup.dispose()
        document.getElementById('popup')?.remove()
    }

    gridInitialized(grid: FlexGrid) {
        grid.hostElement.addEventListener('mousemove', this.showCellPopup.bind(this))
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.get('api/VpEquipment/GetUtilization'),
                    this.services.data.equipmentRequests.queryAll(),
                    this.services.data.equipmentTranfers.queryAll(),
                    this.services.data.get('api/VpEquipment/GetCategories'),
                    this.services.data.vendors.queryAllByAction('GetForRental'),
                    this.services.data.equipmentBreakdowns.queryAll(),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),

                    //this.services.data.post("api/EquipmentAnalytics/GetGpsBasedUtilitzation", {}),
                )
                .subscribe({
                    next: successResult => {
                        this.initialLoadingComplete()
                        this.allJobs = successResult[0]
                        this.grid.items = successResult[1]
                        this.requestGrid.items = this.services.data.equipmentRequests.items
                        this.transferGrid.items = this.services.data.equipmentTranfers.items
                        this.breakdownGrid.items = this.services.data.equipmentBreakdowns.items

                        const jobMap = this.services.data.vpJobs.items.map(x => new ListItem(x.id, x.id + ' ' + x.name))
                        this.jobMap = new DataMap(jobMap, 'value', 'display')
                        const equipmentMap = this.grid.items.map(x => new ListItem(x.equipment, x.description))
                        this.equipmentMap = new DataMap(equipmentMap, 'value', 'display')
                        this.categories = successResult[4]

                        // We want the grid to show a value for any user, even inactive ones.
                        this.allUserMap = new DataMap(successResult[7] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeUserMap = new DataMap(successResult[8] as VpUser[], 'id', 'name')

                        this.requestGrid.selectedItem = undefined
                        this.grid.selectedItem = undefined
                        this.transferGrid.selectedItem = undefined

                        if (this.linkedRequestId) {
                            let activeGrid
                            if (this.active === 2) activeGrid = this.requestGrid
                            else if (this.active === 3) activeGrid = this.transferGrid
                            else if (this.active === 4) activeGrid = this.breakdownGrid

                            const itemToEdit = activeGrid.items.find(x => x.id === this.linkedRequestId)
                            if (itemToEdit) {
                                activeGrid.selectedItem = itemToEdit
                                const row = activeGrid.flexGrid.collectionView.items.indexOf(itemToEdit)
                                activeGrid.flexGrid.select(new CellRange(row, 1), true)
                                if (this.onLoadMarkTransferComplete) {
                                    this.markTransferComplete()
                                }
                            } else this.services.notification.warning('The request from the link could not be found.')
                        }

                        this.grid.view.groupDescriptions.push(new PropertyGroupDescription('mainCategory'))
                        this.grid.view.groupDescriptions.push(new PropertyGroupDescription('category'))
                        this.grid.flexGrid.collapseGroupsToLevel(0)
                    },
                }),
        )
    }

    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem

            cell.classList.remove('yellow-cell')
            cell.classList.remove('wj-has-notes')
            cell.style.backgroundColor = ''
            cell.style.color = ''

            if (col.binding === 'isAvailable' || col.binding === 'scheduledUsage') cell.classList.add('yellow-cell')
            if (col.binding === 'workOrder' && dataItem.workOrder) {
                cell.classList.add('wj-has-notes')
            }
            if (col.binding === 'workOrder' && dataItem.repairColor) {
                cell.style.backgroundColor = dataItem.repairColor
                cell.style.color = 'white'
            }
        }
    }

    transferGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as EquipmentTransfer
            cell.style.textDecoration = 'unset'
            if (dataItem.isComplete) {
                cell.style.textDecoration = 'line-through'
            }
        }
    }

    breakdownGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as EquipmentBreakdown
            cell.style.textDecoration = 'unset'
            if (dataItem.completedDate) {
                cell.style.textDecoration = 'line-through'
            }
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        this.updateRecords([flexGrid.rows[args.row].dataItem])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.equipment)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        ;(flexGrid['controller'] as AvailabilityComponent).itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const controller = flexGrid['controller'] as AvailabilityComponent
        const itemsToSave = controller.grid.items.filter(x => controller.itemIds.indexOf(x.equipment) > -1)
        this.updateRecords(itemsToSave)
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }

    updateRecords(items: any[]) {
        this.services.data.put('api/VpEquipment/BulkUpdateAvailability', items).subscribe({
            error: () => {
                this.services.notification.error('The equipment availability changes were not successfully saved')
            },
        })
    }

    request() {
        this.isAddingRequestFromEquipment = true
        this.requestGrid
            .addItem(false)
            ?.then(() => (this.active = 2))
            .finally(() => (this.isAddingRequestFromEquipment = false))
    }

    approveRequest() {
        const item = this.requestGrid.selectedItem as EquipmentRequest
        if ((!item || !item.equipment || !item.forJob) && item.rentalType === 0) {
            this.services.notification.info('Request approval requires a specific piece of Equipment and a For Job', 'Missing Information')
            return
        }
        if ((!item || !item.forJob) && item.rentalType !== 0) {
            this.services.notification.info('RentalrRequest approval requires a For Job or Rental Company', 'Missing Information')
            return
        }
        const transfer = new EquipmentTransfer()
        transfer.equipment = item.equipment ? item.equipment : item.category
        transfer.forJob = item.forJob
        transfer.fromJob = item.fromJob
        transfer.requestedById = item.requestedById
        transfer.requestedByHrRef = item.requestedByHrRef
        transfer.requestApprovedById = this.services.authentication.userId()
        transfer.requestApprovedByHrRef = this.services.authentication.hrref
        transfer.notes = item.notes
        transfer.transferDate = item.dateNeeded
        this.services.data.equipmentTranfers.add(transfer).subscribe(newTransfer => {
            const deleteAndFinish = () => {
                this.services.data.equipmentRequests.delete(item).subscribe(() => {
                    this.requestGrid.refresh()
                    this.transferGrid.refresh()
                    this.services.notification.success('The Request has been approved and converted to a Transfer.')
                })
            }

            // If the request had images, copy them to the new transfer before deleting the request
            if (item.imageUrls?.length > 0) {
                this.services.data
                    .post('api/EquipmentTransfer/CopyImagesFromRequest', {
                        requestId: item.id,
                        transferId: newTransfer.id,
                    })
                    .subscribe({
                        next: () => deleteAndFinish(),
                        error: () => deleteAndFinish(),
                    })
            } else {
                deleteAndFinish()
            }

            this.active = 3
        })
    }

    markTransferComplete() {
        if (!this.transferGrid.hasItemSelected) return

        const item = this.transferGrid.selectedItem as EquipmentTransfer
        item.isComplete = true
        item.completeDate = new Date()
        this.services.data.equipmentTranfers.edit(item).subscribe({
            next: () => {
                this.services.notification.success('The transfer has been marked complete')
                this.transferGrid.refresh()
            },
            error: () => {
                item.isComplete = false
                item.completeDate = undefined
            },
        })
    }

    markBreakdownComplete() {
        if (!this.breakdownGrid.hasItemSelected) return
        this.services.modals
            .confirm('Comfirm Completion?', 'Are you sure you want to mark the breakdown repair as completed?')
            .then(() => {
                const item = this.breakdownGrid.selectedItem as EquipmentBreakdown
                item.completedDate = new Date()
                this.services.data.equipmentBreakdowns.edit(item).subscribe({
                    next: () => {
                        this.services.notification.success('The breakdown repair has been marked complete')
                        this.breakdownGrid.refresh()
                    },
                    error: () => {
                        item.completedDate = undefined
                    },
                })
            })
            .catch(() => {
                //
            })
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    showCellPopup(e: MouseEvent) {
        const grid = this.grid.flexGrid
        const ht = grid.hitTest(e)
        const rng = ht.range
        const col = grid.columns[rng.col]
        const cell = grid.cells.getCellElement(ht.row, ht.col)

        // Hide the popup if we mouse into a new cell.
        if (ht.cellType !== CellType.Cell) {
            setTimeout(() => this.cellPopup.hide(), 10)
        }
        if (this.currentCell != cell) {
            this.currentCell = cell
            setTimeout(() => this.cellPopup.hide(), 10)
        }

        if (!grid.rows[rng.row] || ht.cellType !== CellType.Cell) return // return if there are no rows

        const rowData = grid.rows[rng.row].dataItem
        if (col && col.binding === 'workOrder') {
            this.cellPopup.owner = cell
            this.popupOrders = rowData.workOrders
            if (this.popupOrders && this.popupOrders.length > 0) {
                setTimeout(() => this.cellPopup.show(), 10)
            }
        }
    }

    openImage(url) {
        window.open('./Resources/Images/EquipmentBreakdown/' + url, '_blank')
    }

    openRequestImage(url) {
        window.open('./Resources/Images/EquipmentRequest/' + url, '_blank')
    }

    openTransferImage(url) {
        window.open('./Resources/Images/EquipmentTransfer/' + url, '_blank')
    }
}
